import { convertCount } from '@lib/numbers/convertCount';
import { FishingWaterDetailAllReviewsFields } from '../../../../../generated/graphql';
import { Button } from '../../../../Clickables/Buttons/Button';
import { Heading } from '../../../../Heading/Heading';
import { StarIcon } from '../../../../Icons/StarIcon';
import { Text } from '../../../../Text/Text';

interface IProps {
  allReviews: FishingWaterDetailAllReviewsFields | null;
  reviewSectionId?: string;
  catchesCount: number;
}

const navigateToReviews = (id: string): void => {
  const element = document.getElementById(id);
  element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/**
 * Component to display ratings and catch count for a fishing water.
 *
 * @param {IProps} props - The properties for the component.
 * @param {FishingWaterDetailAllReviewsFields | null} props.allReviews - The reviews for the fishing water.
 * @param {string} [props.reviewSectionId] - The ID of the review section to navigate to.
 * @param {number} props.catchesCount - The number of catches logged.
 *
 * @returns {JSX.Element} The rendered component.
 */
export const RatingsAndCatchCount = ({ allReviews, catchesCount, reviewSectionId }: IProps) => {
  const hasRatings = !!allReviews?.totalCount && allReviews.totalCount > 0;
  if (catchesCount === 0) {
    return (
      <Heading variant="2xs" className="text-midnight">
        No catches logged yet
      </Heading>
    );
  }
  return (
    <>
      {/* Ratings and CatchCount mobile */}
      <div className="flex items-center justify-center gap-1 md:hidden">
        {hasRatings && (
          <>
            <Text variant="xs" as="span" className="text-clownfish flex">
              <StarIcon height={12} width={12} />
            </Text>
            <Text variant="xs" as="span">
              {allReviews.totalAverage.toFixed(1)}
            </Text>
            <Button
              variant="textNone"
              size="none"
              className="body-text-xs"
              onClick={() => {
                if (reviewSectionId) {
                  navigateToReviews(reviewSectionId);
                }
              }}
              aria-label="navigate to reviews"
            >
              <Text variant="xs" as="span">
                ({allReviews.totalCount} {allReviews.totalCount > 1 ? 'ratings' : 'rating'})
              </Text>
            </Button>
            <Text variant="xs" as="span">{`·`}</Text>
          </>
        )}
        <Text
          variant="xs"
          as="span"
        >{`${catchesCount} ${catchesCount === 1 ? ' catch' : ' catches'}`}</Text>
      </div>
      {/* Ratings and CatchCount desktop */}
      <div className="hidden md:flex md:items-start md:justify-center md:gap-8">
        <div className="flex flex-col items-center">
          <Heading as="span" variant="sm">
            {convertCount(catchesCount)}
          </Heading>
          <Text variant="xs" className="m-0">
            {catchesCount === 1 ? ' Logged catch' : ' Logged catches'}
          </Text>
        </div>
        {hasRatings && (
          <div className="flex flex-col items-center">
            <span className="flex">
              <Heading as="span" variant="sm" className="text-clownfish flex">
                <StarIcon height={33} width={33} />
              </Heading>
              <Heading as="span" variant="sm">
                {allReviews.totalAverage.toFixed(1)}
              </Heading>
            </span>
            <Button
              variant="textNone"
              size="none"
              className="body-text-xs"
              onClick={() => {
                if (reviewSectionId) {
                  navigateToReviews(reviewSectionId);
                }
              }}
              aria-label="navigate to reviews"
            >
              <Text variant="xs" as="span">
                {allReviews.totalCount} {allReviews.totalCount > 1 ? 'ratings' : 'rating'}
              </Text>
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
